/**
 * WeaveIn.ai Platform - Health Plans API Service
 * Manages health plan organizations, LOBs, products, benefits, networks,
 * employer contracts, self-insured plans, member enrollments, and CBO partnerships
 */

import { apiClient, handleApiError } from '../apiClient';

// ============================================================================
// Type Definitions
// ============================================================================

export type PlanType = 'HEALTH_PLAN' | 'TPA' | 'PBM' | 'SELF_INSURED' | 'GOVERNMENT' | 'OTHER';
export type LOBType = 'MEDICAID' | 'MEDICARE' | 'MEDICARE_ADVANTAGE' | 'COMMERCIAL' | 'EXCHANGE' | 'CHIP' | 'DUAL_ELIGIBLE' | 'EMPLOYER_GROUP' | 'INDIVIDUAL' | 'OTHER';
export type ProductType = 'HMO' | 'PPO' | 'EPO' | 'POS' | 'HDHP' | 'CDHP' | 'INDEMNITY' | 'MANAGED_MEDICAID' | 'MEDICARE_HMO' | 'MEDICARE_PPO' | 'OTHER';
export type MetalTier = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'CATASTROPHIC';
export type ContractType = 'FULLY_INSURED' | 'SELF_INSURED' | 'LEVEL_FUNDED' | 'ASO' | 'MINIMUM_PREMIUM';
export type BenefitCategory = 'PREVENTIVE' | 'PRIMARY_CARE' | 'SPECIALIST' | 'EMERGENCY' | 'URGENT_CARE' | 'HOSPITAL_INPATIENT' | 'HOSPITAL_OUTPATIENT' | 'MENTAL_HEALTH' | 'SUBSTANCE_ABUSE' | 'PRESCRIPTION' | 'DENTAL' | 'VISION' | 'REHABILITATION' | 'DURABLE_MEDICAL_EQUIPMENT' | 'HOME_HEALTH' | 'SKILLED_NURSING' | 'MATERNITY' | 'PEDIATRIC' | 'LAB_XRAY' | 'TELEHEALTH' | 'OTHER';

export interface HealthPlan {
  id: number;
  name: string;
  short_name?: string;
  legal_name?: string;
  dba_name?: string;
  plan_type: PlanType;
  naic_code?: string;
  tax_id?: string;
  cms_contract_id?: string;
  state_license_number?: string;
  headquarters_address_line1?: string;
  headquarters_address_line2?: string;
  headquarters_city?: string;
  headquarters_state?: string;
  headquarters_zip?: string;
  main_phone?: string;
  provider_services_phone?: string;
  member_services_phone?: string;
  website?: string;
  claims_address_line1?: string;
  claims_address_line2?: string;
  claims_city?: string;
  claims_state?: string;
  claims_zip?: string;
  electronic_payer_id?: string;
  status: string;
  integration_type?: string;
  api_endpoint?: string;
  settings?: Record<string, any>;
  created_at: string;
  updated_at?: string;
  lines_of_business?: LineOfBusiness[];
  networks?: Network[];
}

export interface LineOfBusiness {
  id: number;
  health_plan_id: number;
  lob_type: LOBType;
  name: string;
  description?: string;
  state?: string;
  region?: string;
  contract_number?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  status: string;
  created_at: string;
}

export interface Product {
  id: number;
  health_plan_id: number;
  lob_id: number;
  product_code: string;
  name: string;
  description?: string;
  product_type: ProductType;
  metal_tier?: MetalTier;
  coverage_start_date?: string;
  coverage_end_date?: string;
  base_premium_individual?: number;
  base_premium_family?: number;
  deductible_individual?: number;
  deductible_family?: number;
  out_of_pocket_max_individual?: number;
  out_of_pocket_max_family?: number;
  network_type?: string;
  status: string;
  lob_type?: string;
  lob_name?: string;
  benefits?: Benefit[];
}

export interface Benefit {
  id: number;
  product_id: number;
  benefit_category: BenefitCategory;
  benefit_name: string;
  description?: string;
  covered: boolean;
  requires_authorization: boolean;
  requires_referral: boolean;
  in_network_copay?: number;
  in_network_coinsurance_pct?: number;
  in_network_deductible_applies: boolean;
  out_network_copay?: number;
  out_network_coinsurance_pct?: number;
  out_network_deductible_applies: boolean;
  annual_limit?: number;
  lifetime_limit?: number;
  limit_unit?: string;
  notes?: string;
  limitations?: string;
  exclusions?: string;
}

export interface Network {
  id: number;
  health_plan_id: number;
  network_code: string;
  name: string;
  description?: string;
  network_type: string;
  geographic_scope?: string;
  states_covered?: string[];
  provider_count?: number;
  facility_count?: number;
  status: string;
  effective_date?: string;
  termination_date?: string;
}

export interface EmployerContract {
  id: number;
  health_plan_id: number;
  employer_id: number;
  employer_name?: string;
  contract_number?: string;
  group_number?: string;
  contract_type: ContractType;
  effective_date: string;
  termination_date?: string;
  renewal_date?: string;
  tpa_id?: number;
  pbm_id?: number;
  stop_loss_carrier?: string;
  specific_deductible?: number;
  aggregate_deductible?: number;
  status: string;
}

export interface EmployerPlanDefinition {
  id: number;
  employer_id: number;
  contract_id?: number;
  plan_code: string;
  name: string;
  description?: string;
  plan_type: ProductType;
  coverage_tier?: string;
  employee_premium?: number;
  employer_premium?: number;
  total_premium?: number;
  deductible_individual?: number;
  deductible_family?: number;
  out_of_pocket_max_individual?: number;
  out_of_pocket_max_family?: number;
  hsa_eligible: boolean;
  hsa_employer_contribution?: number;
  fsa_available?: boolean;
  network_id?: number;
  network_name?: string;
  network_access_type?: string;
  status: string;
  effective_date?: string;
  termination_date?: string;
  benefits?: EmployerPlanBenefit[];
}

export interface EmployerPlanBenefit {
  id: number;
  employer_plan_id: number;
  benefit_category: BenefitCategory;
  benefit_name: string;
  description?: string;
  covered: boolean;
  requires_authorization: boolean;
  requires_referral: boolean;
  in_network_copay?: number;
  in_network_coinsurance_pct?: number;
  in_network_deductible_applies: boolean;
  out_network_copay?: number;
  out_network_coinsurance_pct?: number;
  out_network_deductible_applies: boolean;
  annual_limit?: number;
  lifetime_limit?: number;
  limit_unit?: string;
  notes?: string;
}

export interface MemberEnrollment {
  id: number;
  member_id: number;
  health_plan_product_id?: number;
  employer_plan_id?: number;
  member_id_number?: string;
  group_number?: string;
  subscriber_id?: string;
  relationship_to_subscriber: string;
  subscriber_member_id?: number;
  coverage_tier?: string;
  enrollment_date: string;
  effective_date: string;
  termination_date?: string;
  enrollment_source?: string;
  status: string;
  employee_premium_amount?: number;
  employer_premium_amount?: number;
  cobra_premium_amount?: number;
  cob_order: number;
  product_name?: string;
  product_type?: string;
  health_plan_name?: string;
  employer_plan_name?: string;
}

export interface CBOPartnership {
  id: number;
  health_plan_id: number;
  cbo_id: number;
  cbo_name?: string;
  lob_id?: number;
  lob_type?: string;
  lob_name?: string;
  partnership_type: string;
  contract_number?: string;
  effective_date: string;
  termination_date?: string;
  services_provided?: string[];
  service_areas?: string[];
  reimbursement_model?: string;
  status: string;
}

// ============================================================================
// Create/Update Types
// ============================================================================

export interface HealthPlanCreate {
  name: string;
  short_name?: string;
  legal_name?: string;
  dba_name?: string;
  plan_type?: PlanType;
  naic_code?: string;
  tax_id?: string;
  cms_contract_id?: string;
  state_license_number?: string;
  headquarters_address_line1?: string;
  headquarters_address_line2?: string;
  headquarters_city?: string;
  headquarters_state?: string;
  headquarters_zip?: string;
  main_phone?: string;
  provider_services_phone?: string;
  member_services_phone?: string;
  website?: string;
  claims_address_line1?: string;
  claims_address_line2?: string;
  claims_city?: string;
  claims_state?: string;
  claims_zip?: string;
  electronic_payer_id?: string;
  integration_type?: string;
  api_endpoint?: string;
}

export interface LOBCreate {
  lob_type: LOBType;
  name: string;
  description?: string;
  state?: string;
  region?: string;
  contract_number?: string;
  contract_start_date?: string;
  contract_end_date?: string;
}

export interface ProductCreate {
  lob_id: number;
  product_code: string;
  name: string;
  description?: string;
  product_type: ProductType;
  metal_tier?: MetalTier;
  coverage_start_date?: string;
  coverage_end_date?: string;
  base_premium_individual?: number;
  base_premium_family?: number;
  deductible_individual?: number;
  deductible_family?: number;
  out_of_pocket_max_individual?: number;
  out_of_pocket_max_family?: number;
  network_type?: string;
}

export interface BenefitCreate {
  benefit_category: BenefitCategory;
  benefit_name: string;
  description?: string;
  covered?: boolean;
  requires_authorization?: boolean;
  requires_referral?: boolean;
  in_network_copay?: number;
  in_network_coinsurance_pct?: number;
  in_network_deductible_applies?: boolean;
  out_network_copay?: number;
  out_network_coinsurance_pct?: number;
  out_network_deductible_applies?: boolean;
  annual_limit?: number;
  lifetime_limit?: number;
  limit_unit?: string;
  notes?: string;
}

export interface NetworkCreate {
  network_code: string;
  name: string;
  description?: string;
  network_type: string;
  geographic_scope?: string;
  states_covered?: string[];
  effective_date?: string;
}

export interface EmployerContractCreate {
  employer_id: number;
  contract_number?: string;
  group_number?: string;
  contract_type?: ContractType;
  effective_date: string;
  termination_date?: string;
  renewal_date?: string;
  tpa_id?: number;
  pbm_id?: number;
  stop_loss_carrier?: string;
  specific_deductible?: number;
  aggregate_deductible?: number;
}

export interface EmployerPlanCreate {
  contract_id?: number;
  plan_code: string;
  name: string;
  description?: string;
  plan_type: ProductType;
  coverage_tier?: string;
  employee_premium?: number;
  employer_premium?: number;
  deductible_individual?: number;
  deductible_family?: number;
  out_of_pocket_max_individual?: number;
  out_of_pocket_max_family?: number;
  hsa_eligible?: boolean;
  hsa_employer_contribution?: number;
  network_id?: number;
  effective_date?: string;
}

export interface MemberEnrollmentCreate {
  health_plan_product_id?: number;
  employer_plan_id?: number;
  member_id_number?: string;
  group_number?: string;
  subscriber_id?: string;
  relationship_to_subscriber?: string;
  subscriber_member_id?: number;
  coverage_tier?: string;
  enrollment_date: string;
  effective_date: string;
  termination_date?: string;
  enrollment_source?: string;
  employee_premium_amount?: number;
  employer_premium_amount?: number;
}

// ============================================================================
// API Service
// ============================================================================

export const healthPlansAPI = {
  // ==========================================================================
  // Health Plans CRUD
  // ==========================================================================

  async listHealthPlans(params?: {
    search?: string;
    plan_type?: PlanType;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ plans: HealthPlan[]; total: number }> {
    try {
      const response = await apiClient.get('/api/health-plans', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getHealthPlan(planId: number): Promise<HealthPlan> {
    try {
      const response = await apiClient.get(`/api/health-plans/${planId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createHealthPlan(data: HealthPlanCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post('/api/health-plans', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateHealthPlan(planId: number, data: Partial<HealthPlanCreate>): Promise<{ message: string }> {
    try {
      const response = await apiClient.put(`/api/health-plans/${planId}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async deleteHealthPlan(planId: number): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(`/api/health-plans/${planId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Lines of Business
  // ==========================================================================

  async listLOBs(planId: number): Promise<LineOfBusiness[]> {
    try {
      const response = await apiClient.get(`/api/health-plans/${planId}/lobs`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createLOB(planId: number, data: LOBCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/${planId}/lobs`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Products
  // ==========================================================================

  async listProducts(planId: number, lobId?: number): Promise<Product[]> {
    try {
      const params = lobId ? { lob_id: lobId } : {};
      const response = await apiClient.get(`/api/health-plans/${planId}/products`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getProduct(planId: number, productId: number): Promise<Product> {
    try {
      const response = await apiClient.get(`/api/health-plans/${planId}/products/${productId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createProduct(planId: number, data: ProductCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/${planId}/products`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createBenefit(planId: number, productId: number, data: BenefitCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/${planId}/products/${productId}/benefits`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Networks
  // ==========================================================================

  async listNetworks(planId: number): Promise<Network[]> {
    try {
      const response = await apiClient.get(`/api/health-plans/${planId}/networks`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createNetwork(planId: number, data: NetworkCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/${planId}/networks`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Employer Contracts
  // ==========================================================================

  async listEmployerContracts(planId: number, status?: string): Promise<EmployerContract[]> {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get(`/api/health-plans/${planId}/employer-contracts`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createEmployerContract(planId: number, data: EmployerContractCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/${planId}/employer-contracts`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Employer Plan Definitions (Self-Insured)
  // ==========================================================================

  async listEmployerPlans(employerId: number, status?: string): Promise<EmployerPlanDefinition[]> {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get(`/api/health-plans/employers/${employerId}/plans`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createEmployerPlan(employerId: number, data: EmployerPlanCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/employers/${employerId}/plans`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Member Enrollments
  // ==========================================================================

  async listMemberEnrollments(memberId: number, status?: string): Promise<MemberEnrollment[]> {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get(`/api/health-plans/members/${memberId}/enrollments`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createMemberEnrollment(memberId: number, data: MemberEnrollmentCreate): Promise<{ id: number; message: string }> {
    try {
      const response = await apiClient.post(`/api/health-plans/members/${memberId}/enrollments`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async terminateEnrollment(memberId: number, enrollmentId: number, terminationDate: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.put(
        `/api/health-plans/members/${memberId}/enrollments/${enrollmentId}/terminate`,
        null,
        { params: { termination_date: terminationDate } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // CBO Partnerships
  // ==========================================================================

  async listCBOPartnerships(planId: number, status?: string): Promise<CBOPartnership[]> {
    try {
      const params = status ? { status } : {};
      const response = await apiClient.get(`/api/health-plans/${planId}/cbo-partnerships`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ==========================================================================
  // Employer Benefits Dashboard
  // ==========================================================================

  /**
   * Get the employer associated with the current user's organization.
   * Used to find the correct employer ID for super_admin and ops_admin users.
   */
  async getMyOrganizationEmployer(): Promise<{ employer_id: number | null; employer_name: string | null; employer_number?: string; message?: string }> {
    try {
      const response = await apiClient.get('/api/employers/my-organization/employer');
      return response.data;
    } catch (error) {
      console.warn('Failed to get organization employer:', error);
      return { employer_id: null, employer_name: null, message: 'Failed to fetch employer' };
    }
  },

  async getEmployerDashboard(employerId: number): Promise<EmployerDashboardData> {
    try {
      const response = await apiClient.get(`/api/health-plans/employers/${employerId}/dashboard`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getEmployerPlansWithBenefits(employerId: number): Promise<EmployerPlanWithBenefits[]> {
    try {
      const response = await apiClient.get(`/api/health-plans/employers/${employerId}/plans-with-benefits`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getEmployerEnrollmentStats(employerId: number): Promise<EmployerEnrollmentStats> {
    try {
      const response = await apiClient.get(`/api/health-plans/employers/${employerId}/enrollment-stats`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// ============================================================================
// Employer Dashboard Types
// ============================================================================

export interface EmployerDashboardData {
  employer: {
    id: number;
    name: string;
  };
  summary: {
    total_enrolled: number;
    total_eligible: number;
    enrollment_rate: number;
    active_plans: number;
    total_monthly_premium: number;
    employer_contribution: number;
    employee_contribution: number;
  };
  plans: EmployerPlanSummary[];
  enrollments: {
    plan_id: number;
    plan_name: string;
    coverage_tier: string;
    count: number;
  }[];
  benefits: {
    plan_id: number;
    plan_name: string;
    category: string;
    benefit_name: string;
    in_network: string;
    out_network: string;
  }[];
}

export interface EmployerPlanSummary {
  id: number;
  plan_code: string;
  plan_name: string;
  plan_type: string;
  employee_premium: number;
  employer_premium: number;
  deductible_individual: number;
  deductible_family: number;
  oop_max_individual: number;
  oop_max_family: number;
  hsa_eligible: boolean;
  status: string;
  effective_date?: string;
  termination_date?: string;
  network_name: string;
  carrier_name: string;
  contract_type: string;
  enrolled_count: number;
}

export interface EmployerPlanWithBenefits extends EmployerPlanSummary {
  description?: string;
  hsa_employer_contribution?: number;
  benefits: {
    category: string;
    benefit_name: string;
    description?: string;
    covered: boolean;
    requires_authorization: boolean;
    requires_referral: boolean;
    in_network_copay?: number;
    in_network_coinsurance_pct?: number;
    in_network_deductible_applies: boolean;
    out_network_copay?: number;
    out_network_coinsurance_pct?: number;
    out_network_deductible_applies: boolean;
    annual_limit?: number;
    notes?: string;
  }[];
}

export interface EmployerEnrollmentStats {
  by_plan: {
    plan_id: number;
    plan_name: string;
    plan_type: string;
    enrolled: number;
    total_employee_premium: number;
    total_employer_premium: number;
  }[];
  by_coverage_tier: {
    coverage_tier: string;
    count: number;
    percentage: number;
  }[];
  enrollment_trend: {
    month: string;
    new_enrollments: number;
  }[];
  total_enrolled: number;
}

// ============================================================================
// Constants for UI
// ============================================================================

export const PLAN_TYPES: { value: PlanType; label: string }[] = [
  { value: 'HEALTH_PLAN', label: 'Health Plan' },
  { value: 'TPA', label: 'Third Party Administrator' },
  { value: 'PBM', label: 'Pharmacy Benefit Manager' },
  { value: 'SELF_INSURED', label: 'Self-Insured' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'OTHER', label: 'Other' },
];

export const LOB_TYPES: { value: LOBType; label: string }[] = [
  { value: 'MEDICAID', label: 'Medicaid' },
  { value: 'MEDICARE', label: 'Medicare' },
  { value: 'MEDICARE_ADVANTAGE', label: 'Medicare Advantage' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'EXCHANGE', label: 'Exchange (ACA)' },
  { value: 'CHIP', label: 'CHIP' },
  { value: 'DUAL_ELIGIBLE', label: 'Dual Eligible' },
  { value: 'EMPLOYER_GROUP', label: 'Employer Group' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'OTHER', label: 'Other' },
];

export const PRODUCT_TYPES: { value: ProductType; label: string }[] = [
  { value: 'HMO', label: 'HMO' },
  { value: 'PPO', label: 'PPO' },
  { value: 'EPO', label: 'EPO' },
  { value: 'POS', label: 'POS' },
  { value: 'HDHP', label: 'HDHP' },
  { value: 'CDHP', label: 'CDHP' },
  { value: 'INDEMNITY', label: 'Indemnity' },
  { value: 'MANAGED_MEDICAID', label: 'Managed Medicaid' },
  { value: 'MEDICARE_HMO', label: 'Medicare HMO' },
  { value: 'MEDICARE_PPO', label: 'Medicare PPO' },
  { value: 'OTHER', label: 'Other' },
];

export const METAL_TIERS: { value: MetalTier; label: string; color: string }[] = [
  { value: 'BRONZE', label: 'Bronze', color: '#CD7F32' },
  { value: 'SILVER', label: 'Silver', color: '#C0C0C0' },
  { value: 'GOLD', label: 'Gold', color: '#FFD700' },
  { value: 'PLATINUM', label: 'Platinum', color: '#E5E4E2' },
  { value: 'CATASTROPHIC', label: 'Catastrophic', color: '#808080' },
];

export const CONTRACT_TYPES: { value: ContractType; label: string; description: string }[] = [
  { value: 'FULLY_INSURED', label: 'Fully Insured', description: 'Carrier assumes all risk' },
  { value: 'SELF_INSURED', label: 'Self-Insured', description: 'Employer assumes risk with stop-loss' },
  { value: 'LEVEL_FUNDED', label: 'Level Funded', description: 'Fixed monthly cost with refund potential' },
  { value: 'ASO', label: 'ASO', description: 'Administrative Services Only' },
  { value: 'MINIMUM_PREMIUM', label: 'Minimum Premium', description: 'Hybrid self-insured/fully insured' },
];

export const BENEFIT_CATEGORIES: { value: BenefitCategory; label: string; icon?: string }[] = [
  { value: 'PREVENTIVE', label: 'Preventive Care' },
  { value: 'PRIMARY_CARE', label: 'Primary Care' },
  { value: 'SPECIALIST', label: 'Specialist' },
  { value: 'EMERGENCY', label: 'Emergency Room' },
  { value: 'URGENT_CARE', label: 'Urgent Care' },
  { value: 'HOSPITAL_INPATIENT', label: 'Hospital Inpatient' },
  { value: 'HOSPITAL_OUTPATIENT', label: 'Hospital Outpatient' },
  { value: 'MENTAL_HEALTH', label: 'Mental Health' },
  { value: 'SUBSTANCE_ABUSE', label: 'Substance Abuse' },
  { value: 'PRESCRIPTION', label: 'Prescription Drugs' },
  { value: 'DENTAL', label: 'Dental' },
  { value: 'VISION', label: 'Vision' },
  { value: 'REHABILITATION', label: 'Rehabilitation' },
  { value: 'DURABLE_MEDICAL_EQUIPMENT', label: 'Durable Medical Equipment' },
  { value: 'HOME_HEALTH', label: 'Home Health' },
  { value: 'SKILLED_NURSING', label: 'Skilled Nursing' },
  { value: 'MATERNITY', label: 'Maternity' },
  { value: 'PEDIATRIC', label: 'Pediatric' },
  { value: 'LAB_XRAY', label: 'Lab & X-Ray' },
  { value: 'TELEHEALTH', label: 'Telehealth' },
  { value: 'OTHER', label: 'Other' },
];

export const NETWORK_TYPES: { value: string; label: string }[] = [
  { value: 'PREFERRED', label: 'Preferred' },
  { value: 'STANDARD', label: 'Standard' },
  { value: 'TIERED', label: 'Tiered' },
  { value: 'NARROW', label: 'Narrow' },
  { value: 'BROAD', label: 'Broad' },
  { value: 'CENTER_OF_EXCELLENCE', label: 'Center of Excellence' },
];

export const COVERAGE_TIERS: { value: string; label: string }[] = [
  { value: 'EMPLOYEE_ONLY', label: 'Employee Only' },
  { value: 'EMPLOYEE_SPOUSE', label: 'Employee + Spouse' },
  { value: 'EMPLOYEE_CHILDREN', label: 'Employee + Children' },
  { value: 'FAMILY', label: 'Family' },
];

export const ENROLLMENT_SOURCES: { value: string; label: string }[] = [
  { value: 'OPEN_ENROLLMENT', label: 'Open Enrollment' },
  { value: 'NEW_HIRE', label: 'New Hire' },
  { value: 'QUALIFYING_EVENT', label: 'Qualifying Life Event' },
  { value: 'COBRA', label: 'COBRA' },
  { value: 'SPECIAL_ENROLLMENT', label: 'Special Enrollment' },
];

export const RELATIONSHIP_TYPES: { value: string; label: string }[] = [
  { value: 'SELF', label: 'Self (Subscriber)' },
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'CHILD', label: 'Child' },
  { value: 'DOMESTIC_PARTNER', label: 'Domestic Partner' },
  { value: 'DEPENDENT', label: 'Other Dependent' },
  { value: 'OTHER', label: 'Other' },
];

export default healthPlansAPI;
